import { Injectable, inject, DestroyRef } from '@angular/core';
import { LoggerService } from './logger.service';
import { LogEvent } from '../enums/log-event';
import { BehaviorSubject, interval, Subject, timer, EMPTY } from 'rxjs';
import { takeUntil, take, switchMap, filter, distinctUntilChanged, map } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LiveChatStateService } from 'src/app/modules/live-chat/services/live-chat-state.service';
import { PhoneStatus, StatusOptions } from 'src/app/modules/live-chat/interfaces/phone-status.interface';
import { FeatureToggleService } from './feature-toggle.service';
import { FeatureToggles } from '@core/enums/feature-toggles.enum';

interface PongStatus {
  dashboard: boolean;
  softphone: boolean;
}

interface HealthCheckRecord {
  ping_time: string;
  pong_dash_time: string | null;
  pong_softphone_time: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class IframeHealthCheckService {
  private readonly PING_INTERVAL = 60000;
  private readonly PONG_TIMEOUT = 20000;

  private destroyRef = inject(DestroyRef);
  private logger = inject(LoggerService);
  private liveChatStateService = inject(LiveChatStateService);
  private featureToggleService = inject(FeatureToggleService);

  private pingSubject$ = new Subject<void>();
  private pongDashboardSubject$ = new Subject<void>();
  private pongSoftphoneSubject$ = new Subject<void>();
  private reinstantiatePhoneAppSubject$ = new Subject<void>();

  private isHealthCheckActive = false;
  private stopPing$ = new Subject<void>();
  private currentPingId = 0;
  private hasFailure = false;
  private lastHealthchecks: HealthCheckRecord[] = [];
  private currentHealthcheck: HealthCheckRecord | null = null;
  private messageListener?: (event: MessageEvent) => void;

  public get ping$() {
    return this.pingSubject$.asObservable();
  }
  public get reinstantiatePhoneApp$() {
    return this.reinstantiatePhoneAppSubject$.asObservable();
  }

  
  /**
   * Start the heartbeat health check mechanism
   */
  public start(): void {
    if (this.isHealthCheckActive) {
      return;
    }

    this.isHealthCheckActive = true;
    this.stopPing$ = new Subject<void>();

    this.setupPostMessageListener();
    this.startPingInterval();
  }

  /**
   * Stop the heartbeat health check mechanism
   */
  public stop(): void {
    this.isHealthCheckActive = false;
    this.stopPing$.next();
    this.stopPing$.complete();
    this.removePostMessageListener();
  }

  /**
   * Called when Dashboard sends PONG
   */
  public onDashboardPong(): void {
    this.pongDashboardSubject$.next();
  }

  /**
   * Called when Softphone sends PONG (forwarded by Dashboard)
   */
  public onSoftphonePong(): void {
    this.pongSoftphoneSubject$.next();
  }

  /**
   * Setup listener for postMessage responses from Dashboard
   */
  private setupPostMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      if (event.data && event.data.type === 'PONG:DASHBOARD') {
        this.onDashboardPong();
      } else if (event.data && event.data.type === 'PONG:SOFTPHONE') {
        this.onSoftphonePong();
      }
    };
    window.addEventListener('message', this.messageListener);
  }

  /**
   * Remove postMessage listener
   */
  private removePostMessageListener(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = undefined;
    }
  }

  /**
   * Emit a PING event via postMessage to Dashboard iframe
   */
  private emitPing(): void {
    this.currentPingId++;
    
    // Create a new healthcheck record
    this.currentHealthcheck = {
      ping_time: new Date().toJSON(),
      pong_dash_time: null,
      pong_softphone_time: null,
    };
    
    // Send PING via postMessage to Dashboard iframe
    const dashboardIframe = document.querySelector('#phoneApp iframe') as HTMLIFrameElement;
    if (dashboardIframe && dashboardIframe.contentWindow) {
      dashboardIframe.contentWindow.postMessage(
        { type: 'PING:LIVECHAT' },
        '*'
      );
    }
  }

  /**
   * Start the regular ping interval
   * Only sends PING when phone status is ONLINE and iframes are visible
   */
  private startPingInterval(): void {
    let wasActive = false;

    // Listen to phone status and only ping when online
    this.liveChatStateService.currentPhoneAppStatus$
      .pipe(
        map(status => this.getIsActive(status)),
        distinctUntilChanged(),
        switchMap(isActive => {
          if (!isActive) {
            wasActive = false;
            return EMPTY; // Don't emit when phone is not online
          }
          
          // Phone became active
          if (!wasActive) {
            wasActive = true;
          }
          
          // Phone is active, emit interval
          return interval(this.PING_INTERVAL);
        }),
        takeUntil(this.stopPing$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        // Only send ping if iframes are visible in DOM
        if (this.areIframesVisibleInDom()) {
          this.sendPingAndWaitForPongs();
        }
      });
  }


  private getIsActive(currentStatus?: PhoneStatus){
    return [
      StatusOptions.ONLINE,
      StatusOptions.ON_PAUSE,
      StatusOptions.PAUSE,
    ].includes((currentStatus?.description || '' as any))
  }

  /**
   * Check if phoneApp iframes are visible in the DOM
   * Verifies that:
   * 1. The #phoneApp element exists
   * 3. There are iframes inside it (dashboard/softphone)
   */
  private areIframesVisibleInDom(): boolean {
    try {
      const visibility = this.getIframesVisibilityStatus();
      return visibility.dashboardVisible && visibility.softphoneVisible;
    } catch (error) {
      this.logger.error(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_NOT_RECEIVED_PONG, error as Error);
      return false;
    }
  }

  /**
   * Get individual visibility status of dashboard and softphone iframes
   * Returns an object with boolean flags for each iframe
   */
  private getIframesVisibilityStatus(): { dashboardVisible: boolean; softphoneVisible: boolean } {
    const status = {
      dashboardVisible: false,
      softphoneVisible: false,
    };

    try {
      const phoneAppContainer = document.querySelector('#phoneApp');
      if (!phoneAppContainer) {
        return status;
      }

      const dashboardIframeWindow = (document.querySelector('#phoneApp iframe') as any)?.contentWindow;
      status.dashboardVisible = !!dashboardIframeWindow;

      status.softphoneVisible  = (dashboardIframeWindow?.frames?.length ?? 0) > 0;

    } catch (error) {
       this.logger.error(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_ERROR_CHECKING_IFRAME_VISIBILITY, error as Error)
    }

    return status;
  }

  /**
   * Send PING and wait for both PONGs with timeout
   */
  private sendPingAndWaitForPongs(): void {
    const pongStatus: PongStatus = {
      dashboard: false,
      softphone: false,
    };

    const timeout$ = timer(this.PONG_TIMEOUT).pipe(take(1));
    const stopWaiting$ = new Subject<void>();

    // Listen for Dashboard PONG
    this.pongDashboardSubject$
      .pipe(takeUntil(timeout$), takeUntil(stopWaiting$), take(1))
      .subscribe(() => {
        pongStatus.dashboard = true;
        if (this.currentHealthcheck) {
          this.currentHealthcheck.pong_dash_time = new Date().toJSON();
        }
        this.checkPongStatus(pongStatus, stopWaiting$);
      });

    // Listen for Softphone PONG
    this.pongSoftphoneSubject$
      .pipe(takeUntil(timeout$), takeUntil(stopWaiting$), take(1))
      .subscribe(() => {
        pongStatus.softphone = true;
        if (this.currentHealthcheck) {
          this.currentHealthcheck.pong_softphone_time = new Date().toJSON();
        }
        this.checkPongStatus(pongStatus, stopWaiting$);
      });

    // Handle timeout
    timeout$.subscribe(() => {
      stopWaiting$.next();
      stopWaiting$.complete();
      this.saveCurrentHealthcheck();
      this.handlePongTimeout(pongStatus);
    });

    // Emit the PING
    this.emitPing();
  }

  /**
   * Save current healthcheck record to history
   */
  private saveCurrentHealthcheck(): void {
    if (this.currentHealthcheck) {
      this.lastHealthchecks.push(this.currentHealthcheck);
      
      // Keep only the last 5 records
      if (this.lastHealthchecks.length > 5) {
        this.lastHealthchecks.shift();
      }
      
      this.currentHealthcheck = null;
    }
  }

  /**
   * Check if both PONGs were received
   */
  private checkPongStatus(pongStatus: PongStatus, stopWaiting$: Subject<void>): void {
    if (pongStatus.dashboard && pongStatus.softphone) {
      stopWaiting$.next();
      stopWaiting$.complete();
      this.saveCurrentHealthcheck();
      
      // If there was a previous failure, log recovery
      if (this.hasFailure) {
        this.hasFailure = false;
        this.logger.log(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_RECOVERED, {
          dashboard: true,
          softphone: true,
          lastHealthchecks: [...this.lastHealthchecks],
        });
      }
    }
  }

  /**
   * Handle timeout when PONGs are not received
   */
  private handlePongTimeout(pongStatus: PongStatus): void {
    const failed = !pongStatus.dashboard || !pongStatus.softphone;

    if (!failed) {
      return; // Both PONGs received, no action needed
    }

    // Only log on first failure, not on subsequent failures
    if (!this.hasFailure) {
      this.hasFailure = true;
      const iframesVisibility = this.getIframesVisibilityStatus();
      this.logger.warn(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_NOT_RECEIVED_PONG, {
        dashboard: pongStatus.dashboard,
        softphone: pongStatus.softphone,
        dashboardVisibleInDom: iframesVisibility.dashboardVisible,
        softphoneVisibleInDom: iframesVisibility.softphoneVisible,
        lastHealthchecks: [...this.lastHealthchecks],
      });
      
      this.featureToggleService.someCompanyCanAccess(
        FeatureToggles.ENABLE_LIVECHAT_APPLICATION_MONITORING_FREEZE
      ).pipe(take(1), filter(Boolean)).subscribe(()=>{
        // Trigger phoneApp reinstantiation for automatic recovery
        this.reinstantiatePhoneAppSubject$.next();
        this.logger.info(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_PHONE_APP_REINSTANTIATION_TRIGGERED);
      })
    }
  }
}
